<template>
  <HeaderC />

  <main id="main" class="relative w-full">
    
    <div id="hero" class="relative w-full">
      <HeroComponent v-if="enabled.hero" />
    </div>

    <div id="services" class="relative w-full">
      <ServicesComponent v-if="enabled.services" :popular-only="true" :limit="8" />
    </div>

    <div id="projects" class="relative w-full scroll-mt-20" ref="projectsEl">
      <Suspense>
        <template #default>
          <component v-if="projectsShow" :is="RealisationAsync" />
        </template>
        
        <template #fallback>
          <div class="max-w-7xl mx-auto px-6 lg:px-8 py-20">
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" role="status" aria-live="polite">
              <div v-for="i in 3" :key="'skp-' + i"
                   class="h-[400px] rounded-[2rem] bg-slate-100 animate-pulse border border-slate-200" />
            </div>
          </div>
        </template>
      </Suspense>
    </div>

    <div id="testimonials" class="relative w-full scroll-mt-20" ref="testimonialsEl">
      <Suspense>
        <template #default>
          <component v-if="testimonialsShow" :is="TemoignageAsync" />
        </template>
        
        <template #fallback>
          <div class="max-w-7xl mx-auto px-6 lg:px-8 py-20">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
               <div v-for="i in 3" :key="'skt-'+i" class="h-64 rounded-[2rem] bg-slate-100 animate-pulse border border-slate-200"></div>
            </div>
          </div>
        </template>
      </Suspense>
    </div>
  </main>

  <FooterC />

  <button
    v-show="showTop"
    @click="scrollTop"
    class="fixed bottom-6 right-6 z-50 grid place-items-center h-12 w-12 rounded-full
           text-white bg-slate-900 shadow-xl border border-slate-700
           hover:bg-brand-600 hover:scale-110 active:scale-95 transition-all duration-300"
    aria-label="Remonter en haut">
    <i class="fa-solid fa-arrow-up"></i>
  </button>
</template>

<script>
import { defineComponent, ref, onMounted, onBeforeUnmount, defineAsyncComponent, reactive, computed, watch } from "vue";
import { useStore } from "vuex";
import http from "@/api/http";
import { fetchPageIdByCode } from "@/utils/pageUtils";

/* Above-the-fold */
import HeaderC from "../../components/Visitor_Component/Shared/HighHeader.vue";
import FooterC from "../../components/Visitor_Component/Shared/FooterComponent.vue";
import HeroComponent from "../../components/Visitor_Component/Home_Component/HeroComponent.vue";
import ServicesComponent from "../../components/Visitor_Component/Home_Component/ServicesComponent.vue";

/* Lazy loaded */
const RealisationAsync = defineAsyncComponent(() =>
  import(/* webpackChunkName: "home-realisation" */ "../../components/Visitor_Component/Home_Component/RealisationComponent.vue")
);
const TemoignageAsync = defineAsyncComponent(() =>
  import(/* webpackChunkName: "home-temoignage" */ "../../components/Visitor_Component/Home_Component/TemoignageComponent.vue")
);

export default defineComponent({
  name: "HomeView",
  components: { HeaderC, FooterC, HeroComponent, ServicesComponent },
  setup() {
    const store = useStore();

    // DOM refs observées
    const projectsEl = ref(null);
    const testimonialsEl = ref(null);

    // Visibilité viewport
    const projectsVisible = ref(false);
    const testimonialsVisible = ref(false);

    // Activation depuis la BDD (par défaut ON pour rétro-compat)
    const enabled = reactive({ hero: true, services: true, projects: true, testimonials: true });

    // v-if finaux = enabled && visible (pour les lazy)
    const projectsShow = computed(() => enabled.projects && projectsVisible.value);
    const testimonialsShow = computed(() => enabled.testimonials && testimonialsVisible.value);

    const showTop = ref(false);
    const currentLang = computed(() => store.getters.getLangue || "fr");

    let ioProjects = null;
    let ioTestimonials = null;

    function makeObserver(cb) {
      if (!("IntersectionObserver" in window)) {
        cb();
        return { observe() {}, disconnect() {} };
      }
      const prefersReduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      // On charge un peu avant que ça arrive à l'écran (rootMargin 200px)
      return new IntersectionObserver(
        (entries, obs) =>
          entries.forEach((e) => {
            if (e.isIntersecting) {
              cb();
              obs.unobserve(e.target);
            }
          }),
        { root: null, rootMargin: prefersReduced ? "0px" : "200px", threshold: 0.05 }
      );
    }

    const onScroll = () => { showTop.value = window.scrollY > 420; };

    function scrollTop() {
      const prefersReduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      window.scrollTo({ top: 0, behavior: prefersReduced ? "auto" : "smooth" });
    }

    async function fetchEnabledMap() {
      try {
        const out = await fetchPageIdByCode("home", currentLang.value);
        const pageId = typeof out === "number" ? out : (out?.page_id ?? out?.id ?? null);
        if (!pageId) return;

        const { data } = await http.get("page-components", { params: { page_id: pageId } });
        const items = Array.isArray(data?.items) ? data.items : [];

        enabled.hero = enabled.services = enabled.projects = enabled.testimonials = false;
        for (const it of items) {
          const code = String(it.code || "").trim();
          const on = Number(it.is_enabled) === 1 || it.is_enabled === true;
          if (code in enabled) enabled[code] = !!on;
        }
      } catch (e) {
        console.warn("[HomeView] fetchEnabledMap:", e?.message || e);
      }
    }

    function ensureFirstRevealFallback() {
      setTimeout(() => {
        if (!projectsVisible.value && enabled.projects) projectsVisible.value = true;
        if (!testimonialsVisible.value && enabled.testimonials) testimonialsVisible.value = true;
      }, 500);
    }

    onMounted(async () => {
      if (projectsEl.value && !projectsVisible.value) {
        ioProjects = makeObserver(() => { projectsVisible.value = true; });
        ioProjects.observe(projectsEl.value);
      }
      if (testimonialsEl.value && !testimonialsVisible.value) {
        ioTestimonials = makeObserver(() => { testimonialsVisible.value = true; });
        ioTestimonials.observe(testimonialsEl.value);
      }

      window.addEventListener("scroll", onScroll, { passive: true });
      onScroll();

      await fetchEnabledMap();
      ensureFirstRevealFallback();
    });

    watch(currentLang, async () => {
      await fetchEnabledMap();
      ensureFirstRevealFallback();
    });

    onBeforeUnmount(() => {
      ioProjects && ioProjects.disconnect?.();
      ioTestimonials && ioTestimonials.disconnect?.();
      window.removeEventListener("scroll", onScroll);
    });

    return {
      RealisationAsync,
      TemoignageAsync,
      projectsEl,
      testimonialsEl,
      projectsVisible,
      testimonialsVisible,
      projectsShow,
      testimonialsShow,
      enabled,
      showTop,
      scrollTop,
    };
  },
});
</script>

<style scoped>
/* Les styles sont gérés par Tailwind et les composants enfants */
</style>